import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { HPC_COLORS } from '../hpcTheme';
import {
  calculateBmiResult,
  calculateWhtrResult,
  validateHeightCm,
  validateWeightKg,
  validateWaistCm,
} from './healthCalculations';

const ResultPanel = ({ title, value, status, color, children }) => (
  <Paper
    sx={{
      mt: 2,
      p: 2,
      bgcolor: 'rgba(0,0,0,0.25)',
      border: `1px solid ${color || 'rgba(255,255,255,0.15)'}`,
    }}
  >
    <Typography sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 1 }}>{title}</Typography>
    <Typography sx={{ color: HPC_COLORS.silver, mb: 0.5 }}>
      Value: <strong style={{ color: HPC_COLORS.white }}>{value ?? '—'}</strong>
    </Typography>
    {status && (
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography sx={{ color: HPC_COLORS.silver }}>Status:</Typography>
        <Chip
          label={status}
          size="small"
          sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }}
        />
      </Box>
    )}
    {children}
  </Paper>
);

const HealthCalculators = ({ health }) => {
  const bmiInputsValid =
    !validateHeightCm(health.height_cm) && !validateWeightKg(health.weight_kg);
  const whtrInputsValid =
    !validateHeightCm(health.height_cm) && !validateWaistCm(health.waist_cm);

  const bmiResult = bmiInputsValid
    ? calculateBmiResult(health.weight_kg, health.height_cm)
    : null;
  const whtrResult = whtrInputsValid
    ? calculateWhtrResult(health.waist_cm, health.height_cm)
    : null;

  return (
    <Box mt={3}>
      <Typography variant="subtitle1" sx={{ color: HPC_COLORS.white, fontWeight: 700, mb: 1 }}>
        BMI
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, fontSize: '0.85rem', mb: 1 }}>
        Calculated from height and weight above.
      </Typography>
      {bmiResult?.valid && (
        <ResultPanel
          title="BMI Result"
          value={bmiResult.bmi}
          status={bmiResult.status}
          color={bmiResult.color}
        />
      )}

      <Typography variant="subtitle1" sx={{ color: HPC_COLORS.white, fontWeight: 700, mt: 3, mb: 1 }}>
        Waist-to-Height Ratio (WHtR)
      </Typography>
      <Typography sx={{ color: HPC_COLORS.silver, fontSize: '0.85rem', mb: 1 }}>
        Calculated from height and waist circumference above.
      </Typography>
      {whtrResult?.valid && (
        <ResultPanel
          title="WHtR Result"
          value={whtrResult.whtr}
          status={whtrResult.status}
          color={whtrResult.color}
        >
          <Typography sx={{ color: HPC_COLORS.silver, fontSize: '0.9rem', mb: 1 }}>
            <strong style={{ color: HPC_COLORS.white }}>Interpretation:</strong>{' '}
            {whtrResult.interpretation}
          </Typography>
          <Typography sx={{ color: HPC_COLORS.silver, fontSize: '0.85rem', fontStyle: 'italic' }}>
            Guideline: Keep your waist circumference to less than half of your height.
            {whtrResult.recommendedMaxWaistCm != null && (
              <> Recommended maximum waist: <strong style={{ color: HPC_COLORS.hpuOrange }}>{whtrResult.recommendedMaxWaistCm} cm</strong>.</>
            )}
          </Typography>
        </ResultPanel>
      )}
    </Box>
  );
};

export default HealthCalculators;
